import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getInviteByToken, acceptGuardianInvite } from '../../utils/bookingApi';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader, Skeleton } from '../../components/ui';
import { formatDate } from '../../utils/dates';
import './booking-shared.css';

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    getInviteByToken(token)
      .then(res => setInvite(res.data))
      .catch(() => setError('This invitation link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);
    try {
      await acceptGuardianInvite(token);
      setAccepted(true);
      setTimeout(() => navigate('/booking/my-account'), 2500);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't accept the invitation. Please try again.");
      setAccepting(false);
    }
  };

  const isExpired = invite && invite.expiresAt && new Date(invite.expiresAt) < new Date();
  const isAlreadyAccepted = invite && invite.acceptedAt;
  const loginLink = `/login?returnTo=/booking/accept-invite/${token}`;

  return (
    <div className="bk-page bk-page--narrow">
      <PageHeader title="Guardian invitation" />
      {loading || authLoading ? (
        <Skeleton rows={2} label="Loading the invitation…" />
      ) : (
        <div className="bk-card">
          {error && !invite && <p className="bk-error" role="alert" style={{ margin: 0 }}>{error}</p>}

          {invite && (
            isAlreadyAccepted ? (
              <p className="bk-muted" style={{ margin: 0 }}>This invitation has already been accepted.</p>
            ) : isExpired ? (
              <p className="bk-error" style={{ margin: 0 }}>
                This invitation expired on {formatDate(invite.expiresAt)}. Please ask {invite.invitedBy.firstName} to send a new one.
              </p>
            ) : accepted ? (
              <div role="status">
                <p style={{ fontWeight: 700, color: 'var(--booking-accent)', margin: '0 0 0.5rem' }}>
                  You're now a co-guardian for {invite.gymnast.firstName} {invite.gymnast.lastName}.
                </p>
                <p className="bk-muted" style={{ margin: 0 }}>Taking you to your account…</p>
              </div>
            ) : (
              <>
                <p style={{ margin: '0 0 0.75rem', lineHeight: 1.6 }}>
                  <strong>{invite.invitedBy.firstName} {invite.invitedBy.lastName}</strong> has invited you to become a co-guardian for{' '}
                  <strong>{invite.gymnast.firstName} {invite.gymnast.lastName}</strong>.
                </p>
                <p className="bk-muted" style={{ margin: '0 0 1rem', lineHeight: 1.5 }}>
                  As a co-guardian you'll have the same access: session bookings, progress tracking, health notes and more.
                </p>

                {user ? (
                  <>
                    <p style={{ margin: 0 }}>
                      You're logged in as <strong>{user.firstName} {user.lastName}</strong>.
                    </p>
                    {error && <p className="bk-form-error" role="alert">{error}</p>}
                    <button
                      type="button"
                      className="bk-btn bk-btn--primary bk-btn--full"
                      disabled={accepting}
                      onClick={handleAccept}
                    >
                      {accepting ? 'Accepting…' : 'Accept invitation'}
                    </button>
                    <p className="bk-small" style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                      Not {user.firstName}?{' '}
                      <Link to={loginLink} className="bk-link">Log in with a different account</Link>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="bk-muted" style={{ margin: 0 }}>You need to be logged in to accept this invitation.</p>
                    <div className="bk-actions">
                      <Link to={loginLink} className="bk-btn bk-btn--primary">Log in to accept</Link>
                      <Link to={`/register?returnTo=/booking/accept-invite/${token}`} className="bk-btn">Create an account</Link>
                    </div>
                  </>
                )}
              </>
            )
          )}
        </div>
      )}
    </div>
  );
}
